// Glossaire métier (assurance, courtage Côte d'Ivoire)
// Source unique pour les définitions affichées dans les tooltips d'aide.
//
// Convention : keys en camelCase, alignées sur les noms de champs du modèle.

export const GLOSSAIRE = {
    // ─── Prime & composantes ─────────────────────────────────────────────
    primeNette: {
        label: 'Prime nette',
        short: 'Montant calculé selon le risque, avant taxes et accessoires.',
        long: "La prime nette est le coût technique de l'assurance, calculé par la compagnie en fonction du risque assuré. C'est le point de départ du calcul : on y ajoute ensuite accessoires, taxes, FGA et CEDEAO pour obtenir la prime TTC.",
    },
    primeTTC: {
        label: 'Prime TTC',
        short: 'Montant total à payer par le client (toutes taxes incluses).',
        long: 'Prime TTC = Prime nette + Accessoires (courtier + assureur) + Taxes + FGA + CEDEAO. C\'est le montant total facturé au client.',
    },
    accessoiresCourtier: {
        label: 'Accessoires courtier',
        short: 'Frais facturés par le courtier au-delà de la prime nette.',
        long: 'Frais administratifs perçus par le courtier (frais de dossier, gestion). Ces accessoires constituent un revenu du courtier en plus de sa commission.',
    },
    accessoiresAssureur: {
        label: 'Accessoires assureur',
        short: 'Frais facturés par la compagnie au-delà de la prime nette.',
        long: 'Frais administratifs perçus par la compagnie d\'assurance (frais d\'émission, de gestion). Reversés à l\'assureur.',
    },
    taxes: {
        label: 'Taxes',
        short: 'Taxes réglementaires sur la prime nette (CIMA).',
        long: 'Taxes calculées sur la prime nette selon le code CIMA. Varient selon le type de contrat (auto, vie, IRD, etc.).',
    },
    fga: {
        label: 'FGA',
        short: 'Fonds de Garantie Automobile.',
        long: "Le FGA (Fonds de Garantie des Victimes des Accidents de la Circulation) est un prélèvement obligatoire sur les contrats automobiles, destiné à indemniser les victimes d'accidents impliquant des conducteurs non assurés ou non identifiés.",
    },
    cedeao: {
        label: 'CEDEAO',
        short: 'Carte Brune CEDEAO (couverture inter-États).',
        long: 'Cotisation pour la Carte Brune CEDEAO, qui étend la couverture du contrat automobile aux pays membres de la CEDEAO. Optionnelle mais souvent souscrite pour les déplacements régionaux.',
    },

    // ─── Commission & revenus ────────────────────────────────────────────
    commission: {
        label: 'Commission courtier',
        short: 'Rémunération du courtier prélevée sur la prime nette.',
        long: 'Commission = Prime nette × Taux de commission. Représente la rémunération principale du courtier, négociée avec chaque compagnie.',
    },
    tauxCommission: {
        label: 'Taux de commission',
        short: 'Pourcentage prélevé sur la prime nette comme commission courtier.',
        long: "Taux négocié par contrat-compagnie. Typiquement entre 5% et 30% selon le type d'assurance. Stocké au niveau du contrat pour traçabilité.",
    },
    revenuCourtier: {
        label: 'Revenu courtier',
        short: 'Revenu total du courtier sur ce contrat.',
        long: 'Revenu courtier = Commission + Accessoires courtier. C\'est le total gagné par le cabinet sur la souscription, hors frais de gestion.',
    },
    montantReverseAssureur: {
        label: 'Montant reversé à l\'assureur',
        short: "Montant à reverser à la compagnie d'assurance.",
        long: 'Montant reversé = Prime nette - Commission + Accessoires assureur + Taxes + FGA + CEDEAO. C\'est ce que le courtier doit virer à la compagnie après encaissement client.',
    },

    // ─── Dates contrat ───────────────────────────────────────────────────
    dateEffet: {
        label: "Date d'effet",
        short: 'Date de début de la couverture.',
        long: 'Date à partir de laquelle la garantie est active. Avant cette date, aucun sinistre n\'est couvert.',
    },
    dateEcheance: {
        label: "Date d'échéance",
        short: 'Date de fin de la couverture (à renouveler avant).',
        long: 'Date à laquelle le contrat expire. Le client doit renouveler avant cette date pour éviter une rupture de garantie. Par défaut = dateEffet + durée (en mois).',
    },
    duree: {
        label: 'Durée',
        short: 'Période couverte par le contrat (en mois).',
        long: 'Durée standard : 12 mois pour la plupart des contrats. Peut être réduite (3, 6 mois) pour des couvertures temporaires.',
    },

    // ─── Documents & identifiants ───────────────────────────────────────
    numeroPolice: {
        label: 'N° de police',
        short: 'Identifiant unique du contrat chez la compagnie.',
        long: "Numéro attribué par la compagnie d'assurance à chaque contrat. Sert de référence pour tous les échanges (sinistre, renouvellement, attestation).",
    },
    numeroAttestation: {
        label: "N° d'attestation",
        short: 'Identifiant du document délivré au client.',
        long: 'Numéro de l\'attestation d\'assurance physique remise au client (pour automobile : à présenter aux forces de l\'ordre).',
    },
    numeroCedeao: {
        label: 'N° CEDEAO',
        short: 'Numéro de la Carte Brune CEDEAO si souscrite.',
        long: "Numéro de la Carte Brune permettant de prouver la couverture du véhicule lors de déplacements dans un pays membre de la CEDEAO.",
    },

    // ─── Règlements ──────────────────────────────────────────────────────
    totalVerse: {
        label: 'Total versé',
        short: 'Somme des paiements déjà reçus du client.',
        long: 'Cumul de tous les règlements enregistrés sur ce contrat. Comparé à la Prime TTC pour calculer le reste à payer.',
    },
    resteAPayer: {
        label: 'Reste à payer',
        short: 'Solde dû par le client.',
        long: 'Reste à payer = Prime TTC - Total versé. Si > 0 → contrat partiellement payé ou impayé. Si = 0 → contrat soldé.',
    },
    moyenPaiement: {
        label: 'Mode de paiement',
        short: 'Espèces, Chèque, Virement ou Mobile Money.',
        long: "Mode de règlement utilisé pour ce versement. En Côte d'Ivoire, Mobile Money (Orange Money, MTN MoMo, Moov Money, Wave) représente une part majoritaire des encaissements.",
    },
    tropPercu: {
        label: 'Trop-perçu',
        short: 'Montant versé en plus du reste dû.',
        long: 'Si le client paie plus que le reste à payer (acompte pour le renouvellement, erreur, etc.), le surplus est enregistré comme trop-perçu. À régulariser ensuite.',
    },

    // ─── Sinistres ───────────────────────────────────────────────────────
    sinistre: {
        label: 'Sinistre',
        short: 'Événement déclaré ouvrant droit à indemnisation.',
        long: 'Tout événement (accident, vol, dégât) survenu pendant la période de couverture et déclaré dans les délais légaux. Doit être instruit puis indemnisé selon les garanties souscrites.',
    },
    montantEstime: {
        label: 'Montant estimé',
        short: 'Évaluation initiale du préjudice par le client/courtier.',
        long: "Montant indicatif déclaré à l'ouverture du sinistre. Sert d'estimation provisoire. Le montant définitif est fixé par l'expert / la compagnie.",
    },
    indemnisation: {
        label: 'Indemnisation',
        short: 'Montant versé par la compagnie après instruction.',
        long: "Montant définitif réglé par la compagnie d'assurance au bénéficiaire (assuré ou tiers) après expertise et application des garanties / franchises.",
    },

    // ─── Statuts contrat ─────────────────────────────────────────────────
    statutActif: {
        label: 'Statut Actif',
        short: 'Contrat en cours de validité.',
        long: 'Le contrat est entré en effet et son échéance n\'est pas dépassée. Couvre les sinistres survenant dans la période.',
    },
    statutExpire: {
        label: 'Statut Expiré',
        short: 'Date d\'échéance dépassée sans renouvellement.',
        long: 'Le contrat a atteint sa date d\'échéance et n\'a pas été renouvelé. Les sinistres postérieurs ne sont plus couverts.',
    },
    statutResilie: {
        label: 'Statut Résilié',
        short: 'Contrat interrompu avant échéance.',
        long: 'Le contrat a été résilié à la demande du client ou de la compagnie avant sa date d\'échéance. Les sinistres après résiliation ne sont plus couverts.',
    },
};

// Helper : récupère une définition (ou null si terme inconnu)
export const getTerme = (key) => GLOSSAIRE[key] || null;
